import { useRef, useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { ArrowLeft, Upload, X } from 'lucide-react';
import {
  useAnnouncement,
  useCreateAnnouncement,
  useUpdateAnnouncement,
  useInitAnnouncementAttachment,
  useCountries,
  useDepartments,
} from '@/lib/queries';

export function AnnouncementFormPage() {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();
  const { data: existing } = useAnnouncement(id);
  const { data: countries } = useCountries();
  const { data: departments } = useDepartments();
  const create = useCreateAnnouncement();
  const update = useUpdateAnnouncement();
  const initAttach = useInitAnnouncementAttachment();
  const fileRef = useRef<HTMLInputElement>(null);

  const [form, setForm] = useState({
    title: '',
    body: '',
    pinned: false,
    audienceType: 'ALL' as string,
    audienceCountryIds: [] as string[],
    audienceDepartmentIds: [] as string[],
  });
  const [attachFile, setAttachFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Populate form when editing
  useEffect(() => {
    if (existing && isEdit) {
      setForm({
        title: existing.title ?? '',
        body: existing.body ?? '',
        pinned: existing.pinned ?? false,
        audienceType: existing.audienceType ?? 'ALL',
        audienceCountryIds: existing.audienceCountryIds ?? [],
        audienceDepartmentIds: existing.audienceDepartmentIds ?? [],
      });
    }
  }, [existing, isEdit]);

  const set = (k: string, v: any) => setForm((f) => ({ ...f, [k]: v }));

  const toggleArrayItem = (key: 'audienceCountryIds' | 'audienceDepartmentIds', val: string) => {
    setForm((f) => ({
      ...f,
      [key]: f[key].includes(val) ? f[key].filter((x) => x !== val) : [...f[key], val],
    }));
  };

  const submit = async (status: 'DRAFT' | 'PUBLISHED') => {
    setError(null);
    setSaving(true);
    try {
      const payload = { ...form, status };
      let announcementId: string;

      if (isEdit) {
        await update.mutateAsync({ id: id!, payload });
        announcementId = id!;
      } else {
        const result = await create.mutateAsync(payload);
        announcementId = result.id;
      }

      // Upload attachment if selected
      if (attachFile) {
        const { uploadUrl } = await initAttach.mutateAsync({
          id: announcementId,
          payload: {
            fileName: attachFile.name,
            mimeType: attachFile.type || 'application/octet-stream',
            size: attachFile.size,
          },
        });
        const putRes = await fetch(uploadUrl, {
          method: 'PUT',
          body: attachFile,
          headers: { 'Content-Type': attachFile.type || 'application/octet-stream' },
        });
        if (!putRes.ok) throw new Error('No se pudo subir el adjunto');
      }

      navigate(`/anuncios/${announcementId}`);
    } catch (err: any) {
      setError(err?.response?.data?.error?.message ?? err.message ?? 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <Link
        to="/anuncios"
        className="inline-flex items-center gap-2 text-sm font-medium text-font-secondary hover:text-primary"
      >
        <ArrowLeft size={16} /> Volver a anuncios
      </Link>

      <h1 className="text-2xl font-bold text-font-primary">
        {isEdit ? 'Editar anuncio' : 'Nuevo anuncio'}
      </h1>

      <div className="rounded-lg border border-border bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-5">
          {/* Title */}
          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-semibold text-font-primary">
              Título <span className="text-error">*</span>
            </span>
            <input
              className="input h-10"
              maxLength={200}
              value={form.title}
              onChange={(e) => set('title', e.target.value)}
              placeholder="Ej: Actualización de políticas de vacaciones 2026"
            />
            <span className="text-[10px] text-font-tertiary">
              {form.title.length}/200
            </span>
          </label>

          {/* Body */}
          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-semibold text-font-primary">
              Contenido <span className="text-error">*</span>
            </span>
            <textarea
              className="input min-h-[200px]"
              maxLength={5000}
              value={form.body}
              onChange={(e) => set('body', e.target.value)}
              placeholder="Escribe el contenido del anuncio. Usa guiones (- ) para listas."
            />
            <span className="text-[10px] text-font-tertiary">
              {form.body.length}/5000
            </span>
          </label>

          {/* Audience */}
          <div className="flex flex-col gap-1.5">
            <span className="text-xs font-semibold text-font-primary">Audiencia</span>
            <select
              className="input h-10 max-w-sm"
              value={form.audienceType}
              onChange={(e) => {
                const v = e.target.value;
                setForm((f) => ({
                  ...f,
                  audienceType: v,
                  ...(v === 'ALL' && { audienceCountryIds: [], audienceDepartmentIds: [] }),
                  ...(v === 'BY_COUNTRY' && { audienceDepartmentIds: [] }),
                  ...(v === 'BY_DEPARTMENT' && { audienceCountryIds: [] }),
                }));
              }}
            >
              <option value="ALL">Todos los colaboradores</option>
              <option value="BY_COUNTRY">Por país</option>
              <option value="BY_DEPARTMENT">Por departamento</option>
              <option value="BY_COUNTRY_AND_DEPARTMENT">Por país y departamento</option>
            </select>

            {(form.audienceType === 'BY_COUNTRY' ||
              form.audienceType === 'BY_COUNTRY_AND_DEPARTMENT') && (
              <div className="mt-2">
                <p className="mb-1 text-[10px] font-semibold uppercase text-font-tertiary">
                  Países
                </p>
                <div className="flex flex-wrap gap-2">
                  {countries?.items?.map((c: any) => (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => toggleArrayItem('audienceCountryIds', c.id)}
                      className={`rounded-full border px-3 py-1 text-xs font-semibold transition ${
                        form.audienceCountryIds.includes(c.id)
                          ? 'border-primary bg-primary-subtle text-primary'
                          : 'border-border bg-white text-font-secondary hover:border-primary'
                      }`}
                    >
                      {c.nameEs}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {(form.audienceType === 'BY_DEPARTMENT' ||
              form.audienceType === 'BY_COUNTRY_AND_DEPARTMENT') && (
              <div className="mt-2">
                <p className="mb-1 text-[10px] font-semibold uppercase text-font-tertiary">
                  Departamentos
                </p>
                <div className="flex flex-wrap gap-2">
                  {departments?.items?.map((d: any) => (
                    <button
                      key={d.id}
                      type="button"
                      onClick={() => toggleArrayItem('audienceDepartmentIds', d.id)}
                      className={`rounded-full border px-3 py-1 text-xs font-semibold transition ${
                        form.audienceDepartmentIds.includes(d.id)
                          ? 'border-primary bg-primary-subtle text-primary'
                          : 'border-border bg-white text-font-secondary hover:border-primary'
                      }`}
                    >
                      {d.name}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Attachment */}
          <div className="flex flex-col gap-1.5">
            <span className="text-xs font-semibold text-font-primary">
              Adjunto (opcional)
            </span>
            {attachFile ? (
              <div className="flex items-center gap-3 rounded-md border border-border bg-bg p-3">
                <span className="text-sm text-font-primary">{attachFile.name}</span>
                <span className="text-xs text-font-tertiary">
                  ({(attachFile.size / 1024).toFixed(0)} KB)
                </span>
                <button
                  type="button"
                  onClick={() => {
                    setAttachFile(null);
                    if (fileRef.current) fileRef.current.value = '';
                  }}
                  className="ml-auto text-error hover:text-error"
                >
                  <X size={14} />
                </button>
              </div>
            ) : (
              <>
                <input
                  ref={fileRef}
                  type="file"
                  className="hidden"
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) {
                      if (f.size > 10 * 1024 * 1024) {
                        alert('El archivo no debe superar 10 MB');
                        return;
                      }
                      setAttachFile(f);
                    }
                  }}
                />
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  className="flex h-10 w-fit items-center gap-2 rounded-md border border-dashed border-border bg-bg px-4 text-xs font-semibold text-font-secondary hover:border-primary hover:text-primary"
                >
                  <Upload size={14} /> Seleccionar archivo (máx 10 MB)
                </button>
              </>
            )}
            {isEdit && existing?.attachmentName && !attachFile && (
              <p className="text-[10px] text-font-tertiary">
                Adjunto actual: {existing.attachmentName}. Sube uno nuevo para reemplazarlo.
              </p>
            )}
          </div>

          {/* Pinned */}
          <label className="flex items-center gap-3">
            <input
              type="checkbox"
              checked={form.pinned}
              onChange={(e) => set('pinned', e.target.checked)}
              className="h-4 w-4 rounded border-border text-primary focus:ring-primary"
            />
            <span className="text-sm font-semibold text-font-primary">
              Fijar este anuncio arriba de la lista
            </span>
          </label>

          {error && (
            <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-error">
              {error}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="mt-8 flex justify-end gap-3 border-t border-border pt-5">
          <Link
            to="/anuncios"
            className="flex h-10 items-center rounded-md border border-border bg-white px-4 text-sm font-semibold text-font-secondary hover:bg-bg"
          >
            Cancelar
          </Link>
          <button
            onClick={() => submit('DRAFT')}
            disabled={saving || !form.title.trim() || !form.body.trim()}
            className="flex h-10 items-center rounded-md border border-border bg-white px-4 text-sm font-semibold text-font-primary hover:bg-bg disabled:opacity-60"
          >
            {saving ? 'Guardando...' : 'Guardar borrador'}
          </button>
          <button
            onClick={() => submit('PUBLISHED')}
            disabled={saving || !form.title.trim() || !form.body.trim()}
            className="flex h-10 items-center rounded-md bg-primary px-6 text-sm font-semibold text-white hover:bg-primary-dark disabled:opacity-60"
          >
            {saving ? 'Publicando...' : 'Publicar'}
          </button>
        </div>
      </div>
    </div>
  );
}
